'use client';

import * as React from 'react';
import {
  ActivityIndicator,
  ActivityTrace,
  type ActivityTraceItem,
  type Attachment,
  Avatar,
  Banner,
  ChatHeader,
  Composer,
  FileViewer,
  HilApprovalCard,
  type HilDecisionResult,
  MessageBubble,
  ProcessDisclosure,
  QuickReplies,
} from '@langbridge-builder/design-system';
import type {
  Activity,
  ActivityBlock,
  ChatMessage,
  ChatPhase,
  FileBlock,
  InterruptBlock,
  InterruptDecision,
  MessageBlock,
  PendingInterrupt,
} from '../state/reducer';
import { cn } from '../lib/cn';
import { showsAvatar } from '../lib/grouping';
import { MessageList } from './message-list';
import { MarkdownMessage } from './markdown';

export type ChatViewMode = 'panel' | 'full';

export interface ChatViewProps {
  mode?: ChatViewMode;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  messages: ChatMessage[];
  phase: ChatPhase;
  /** Live streaming activity (drives the ActivityIndicator before the answer). */
  activity?: Activity | null;
  /** Pending HIL interrupt (deep_agent). When set, an approval prompt is shown. */
  interrupt?: PendingInterrupt | null;
  /** Answer the pending interrupt; resumes the paused turn. */
  onRespondInterrupt?: (decisions: InterruptDecision[]) => void;
  error?: string;
  onSend: (text: string, attachments?: Attachment[]) => void;
  onRetry?: () => void;
  /**
   * Multimodal attachment capability for the composer, derived from the agent's
   * IO contract. When `accept` is true, the attach button / paste / drag-drop are
   * enabled. `mode` is `'url'` for workflow agents that only take image URLs;
   * otherwise `'file'` (inline upload). Omit to disable attachments.
   */
  attach?: { accept: boolean; mode?: 'file' | 'url'; mimeTypes?: string[] };
  onAbort?: () => void;
  onClose?: () => void;
  /** Shown only before the first message. */
  quickReplies?: string[];
  onQuickReply?: (text: string) => void;
  /**
   * Render an assistant turn's A2UI (generative UI) surface. Called per message
   * with that turn's server messages, so the surface renders inline in the
   * transcript (like a bubble) rather than floating at the bottom. Injected by
   * the consumer to keep @a2ui/* out of the default (widget/S2) bundle.
   */
  renderA2ui?: (messages: Record<string, unknown>[]) => React.ReactNode;
  /** Generic bottom slot for non-A2UI rich content (e.g. demo cards). */
  renderRichContent?: () => React.ReactNode;
  /**
   * Reveal a `show_file` file in the OS file explorer. Only wired by the desktop
   * app (the file is a real local file). When omitted, the reveal button hides.
   * Called with the file's absolute `uri` (present only when the backend knows it).
   */
  onRevealFile?: (file: { path: string; uri: string }) => void;
  /** S2 web-app shell provides its own top bar, so it passes false. Default true (panel/preview). */
  showHeader?: boolean;
  /** S3 panel empty-state greeting bubble (shown before the first message). */
  greeting?: React.ReactNode;
  /** "⚡ Powered by Langbridge" footer. Defaults to true in panel (S3), false in full (S2). */
  poweredBy?: boolean;
  /** Small note shown before "Powered by" in the footer (e.g. S2 AI disclaimer). */
  disclaimer?: React.ReactNode;
  className?: string;
}

/** Centered reading column for S2 full mode (messages + composer). */
const COLUMN = 'mx-auto w-full max-w-[var(--chat-column-width)] px-[var(--chat-column-px)]';

/** Sent user attachments: image thumbnails + file chips, right-aligned above the bubble. */
function UserAttachments({ items }: { items: Attachment[] }) {
  return (
    <div className="flex w-full flex-wrap justify-end gap-2">
      {items.map((a) =>
        a.kind === 'image' && a.previewUrl ? (
          <div
            key={a.id}
            className="size-[72px] overflow-hidden rounded-[var(--radius-md)] border border-border bg-muted"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={a.previewUrl} alt={a.name} className="size-full object-cover" />
          </div>
        ) : (
          <div
            key={a.id}
            className="flex h-[40px] max-w-[180px] items-center gap-2 rounded-[var(--radius-md)] border border-border bg-muted px-2 text-2xs text-fg"
          >
            <span className="truncate font-medium">{a.name}</span>
          </div>
        ),
      )}
    </div>
  );
}

/** Inline sparkles glyph (lucide-shaped) — avoids a lucide-react dep in chat-ui. */
function SparkGlyph({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m12 3 1.9 5.8a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-1.9a2 2 0 0 0 1.3-1.3Z" />
    </svg>
  );
}

/**
 * S2 empty-state hero: logo slot + welcome + 2×2 starter grid. Shown in full
 * mode before the first message. Assembly-only — references design-system tokens.
 */
function EmptyHero({ starters, onPick }: { starters: string[]; onPick: (text: string) => void }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-4 py-[var(--space-8)] text-center">
      <div className="flex size-[60px] items-center justify-center rounded-md bg-accent text-2xl font-extrabold text-accent-fg">
        L
      </div>
      <h1 className="mt-[var(--space-4)] text-3xl font-extrabold tracking-[-0.02em] text-fg">
        무엇을 도와드릴까요?
      </h1>
      <p className="mb-[var(--space-6)] mt-[var(--space-2)] max-w-[44ch] text-md text-fg-secondary">
        Langbridge 도우미예요. 아래 주제로 바로 시작하거나 직접 입력해 보세요.
      </p>
      {starters.length > 0 ? (
        <div className="grid w-full max-w-[560px] grid-cols-2 gap-[var(--space-2-5)]">
          {starters.slice(0, 4).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onPick(s)}
              className="flex items-center gap-2.5 rounded-card border border-border bg-surface px-[var(--space-3-5)] py-[var(--space-3)] text-left text-sm font-medium text-fg transition-colors hover:border-accent hover:bg-surface-2"
            >
              <SparkGlyph className="size-4 shrink-0 text-accent" />
              {s}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** The activity-bearing variant of an ActivityTrace item (has a nestable trace). */
type ActivityItem = Extract<ActivityTraceItem, { activity: unknown }>;

/** Pretty-print streamed tool-arg JSON; fall back to raw for partial streams. */
function prettyArgs(text: string): string {
  const t = text.trim();
  if (!t) return text;
  try {
    return JSON.stringify(JSON.parse(t), null, 2);
  } catch {
    return text; // mid-stream / non-JSON — show as-is
  }
}

/** Map an ActivityBlock → ActivityIndicator props (done → ✓ trace + duration). */
function toActivityItem(b: ActivityBlock): ActivityItem {
  const done = b.state === 'done';
  const activity: ActivityItem['activity'] = {
    status: done ? 'done' : b.status,
    kind: b.status,
    detail: b.detail,
    duration: b.durationSec,
    // While live, the indicator ticks a real-time "· 2.4s" from startedAt;
    // it's ignored once done (the stamped durationSec shows instead).
    startedAt: b.startedAt,
  };
  // Steps are phases, not tools — show just the phase name ("계획 수립 · 1.2s")
  // instead of the tool-flavored "…사용함" verb reserved for real tool calls.
  if (done && b.status === 'step' && b.detail) activity.label = b.detail;
  return { activity };
}

/** Markdown-family extensions get prose rendering; everything else stays mono. */
const MD_EXT = /\.(md|markdown|mdx)$/i;

/** A show_file block → an inline FileViewer card (markdown body for .md, else raw). */
function toFileNode(
  b: FileBlock,
  onRevealFile?: (file: { path: string; uri: string }) => void,
): React.ReactNode {
  const isMarkdown =
    MD_EXT.test(b.path) || (!b.path.includes('.') && /^#|\n#|\*\*|\n- /.test(b.content));
  // Only offer "reveal in explorer" when the consumer supports it AND the backend
  // gave us an absolute path to reveal (otherwise the button would do nothing).
  const uri = b.uri;
  const onReveal = onRevealFile && uri ? () => onRevealFile({ path: b.path, uri }) : undefined;
  // mode="auto": the card adapts (header labels / layout) to its real width via a
  // @container query — full column (S2) shows labels, narrow panel (S3) icon-only.
  return (
    <FileViewer path={b.path} title={b.title} content={b.content} onReveal={onReveal}>
      {isMarkdown ? <MarkdownMessage text={b.content} /> : undefined}
    </FileViewer>
  );
}

/** An ActivityBlock that captured streamed chunks → a collapsible ProcessDisclosure. */
function toDisclosure(b: ActivityBlock): React.ReactNode {
  const done = b.state === 'done';
  const isArgs = b.chunkKind === 'args';
  return (
    <ProcessDisclosure
      kind={isArgs ? 'args' : 'reasoning'}
      // tool-args → the tool's friendly name; a named block (e.g. sub-agent answer)
      // keeps its detail; plain reasoning → default while live, settled label when done.
      label={b.detail ?? (isArgs ? undefined : done ? '생각 정리' : undefined)}
      chunks={isArgs ? prettyArgs(b.chunks ?? '') : (b.chunks ?? '')}
      status={done ? 'done' : 'active'}
      duration={b.durationSec}
      startedAt={b.startedAt}
    />
  );
}

/**
 * Flatten the assistant's blocks into a nested ActivityTrace tree: text answers
 * break out full-width; activity lines group under the timeline rail; blocks
 * stamped with a deeper `depth` (sub-agents) nest under the preceding parent.
 */
function buildTraceItems(
  blocks: MessageBlock[],
  onRevealFile?: (file: { path: string; uri: string }) => void,
): ActivityTraceItem[] {
  const root: ActivityTraceItem[] = [];
  let containerAtDepth: Record<number, ActivityTraceItem[]> = { 0: root };
  /** Nearest open rail container at or above `depth` (fills gaps in nesting). */
  const containerFor = (depth: number): ActivityTraceItem[] => {
    for (let d = depth; d >= 0; d--) if (containerAtDepth[d]) return containerAtDepth[d]!;
    return root;
  };
  /** True if a block carries no renderable info (empty settled bracket). */
  const isOrphan = (b: ActivityBlock): boolean =>
    b.state === 'done' && !b.detail && !(b.chunks && b.chunks.length > 0);
  /** Depth of the next activity block, or -1 if an answer breaks the rail / none. */
  const nextActivityDepth = (from: number): number => {
    for (let j = from + 1; j < blocks.length; j++) {
      const nb = blocks[j]!;
      if (nb.kind === 'interrupt' || nb.kind === 'a2ui') continue;
      if (nb.kind === 'text') {
        if (!nb.text) continue;
        return -1;
      }
      if (nb.kind === 'file') return -1;
      if (isOrphan(nb)) continue;
      return nb.depth ?? 0;
    }
    return -1;
  };
  /** End time (ms epoch) of a settled activity block; falls back to its start. */
  const endOf = (b: ActivityBlock): number | undefined => {
    if (b.state === 'done' && b.startedAt != null && b.durationSec != null) {
      return b.startedAt + b.durationSec * 1000;
    }
    return b.startedAt;
  };
  /**
   * Roll a parent's elapsed time up over its sub-agent subtree: the parent should
   * span from its (or its earliest descendant's) start to the last descendant's
   * end, and stay "in progress" while ANY descendant is still running — so the
   * time hierarchy matches the visual (rail) hierarchy.
   */
  const rollup = (idx: number, parentDepth: number) => {
    const starts: number[] = [];
    const ends: number[] = [];
    let active = false;
    const consider = (bl: ActivityBlock) => {
      if (bl.startedAt != null) starts.push(bl.startedAt);
      const e = endOf(bl);
      if (e != null) ends.push(e);
      if (bl.state !== 'done') active = true;
    };
    consider(blocks[idx] as ActivityBlock);
    for (let j = idx + 1; j < blocks.length; j++) {
      const nb = blocks[j]!;
      if (nb.kind === 'text') {
        if (nb.text) break; // an answer breaks the subtree
        continue;
      }
      if (nb.kind === 'file') break;
      if (nb.kind === 'interrupt' || nb.kind === 'a2ui') continue;
      if ((nb.depth ?? 0) <= parentDepth) break; // back to a sibling/ancestor level
      if (isOrphan(nb)) continue;
      consider(nb);
    }
    return {
      minStart: starts.length ? Math.min(...starts) : undefined,
      maxEnd: ends.length ? Math.max(...ends) : undefined,
      active,
    };
  };

  blocks.forEach((b, i) => {
    if (b.kind === 'interrupt') return; // rendered as its own inline card
    if (b.kind === 'a2ui') return; // rendered as its own inline surface segment
    if (b.kind === 'text') {
      if (!b.text) return;
      root.push({ node: <MarkdownMessage text={b.text} />, role: 'answer' });
      containerAtDepth = { 0: root }; // an answer breaks all open rail groups
      return;
    }
    // A show_file viewer card breaks the rail full-width, like an answer.
    if (b.kind === 'file') {
      root.push({ node: toFileNode(b, onRevealFile), role: 'answer' });
      containerAtDepth = { 0: root };
      return;
    }
    // A settled trace with no name carries no info ("완료 · 0.0s") and reads as a
    // broken orphan line (e.g. a resume run's transient bracket) — skip it.
    if (isOrphan(b)) return;

    const depth = b.depth ?? 0;
    const hasChunks = !!(b.chunks && b.chunks.length > 0);
    // A block that opens a deeper level (e.g. `task` delegating to a sub-agent)
    // is the rail PARENT: render its label as the line and nest the sub-agent's
    // work beneath it. Its own args ride along as the first, collapsed disclosure.
    const isParent = nextActivityDepth(i) > depth;
    const container = containerFor(depth);

    if (isParent) {
      const item = toActivityItem(b);
      if (b.detail) item.activity.label = b.detail; // "하위 작업 실행" (no "…사용함" verb)
      // Roll the elapsed time up over the sub-agent subtree so a parent that
      // contains 5s of child work reads as 5s (not 0.0s), and keeps a live
      // spinner + ticking timer while any descendant is still running.
      const { minStart, maxEnd, active } = rollup(i, depth);
      if (active) {
        item.activity.status = b.status; // not 'done' → spinner + LiveDuration
        item.activity.duration = undefined;
        if (minStart != null) item.activity.startedAt = minStart;
      } else {
        item.activity.status = 'done';
        if (minStart != null) item.activity.startedAt = minStart;
        if (minStart != null && maxEnd != null) {
          item.activity.duration = Math.max(0, (maxEnd - minStart) / 1000);
        }
      }
      const trace: ActivityTraceItem[] = [];
      item.trace = trace;
      container.push(item);
      containerAtDepth[depth + 1] = trace;
      if (hasChunks) trace.push({ node: toDisclosure(b), role: 'process' });
      return;
    }

    // A chunk-bearing block becomes a self-contained ProcessDisclosure (its header
    // IS the activity line). At depth 0 it breaks the rail full-width like an
    // answer; nested (sub-agent) it stays inside the parent's rail as a process line.
    if (hasChunks) {
      container.push({ node: toDisclosure(b), role: depth > 0 ? 'process' : 'answer' });
      if (depth === 0) containerAtDepth = { 0: root };
      return;
    }

    const item = toActivityItem(b);
    const trace: ActivityTraceItem[] = [];
    item.trace = trace;
    container.push(item);
    containerAtDepth[depth + 1] = trace;
  });
  return root;
}

type MessageSegment =
  | { type: 'content'; blocks: MessageBlock[] }
  | { type: 'interrupt'; block: InterruptBlock }
  | { type: 'a2ui' };

/**
 * Split an assistant turn at inline interrupt + A2UI markers, so each renders in
 * chronological order → [process trace → surface/card → resumed answer]. Both
 * markers break the surrounding text into separate content segments.
 */
function splitSegments(blocks: MessageBlock[]): MessageSegment[] {
  const segs: MessageSegment[] = [];
  let buf: MessageBlock[] = [];
  const flush = () => {
    if (buf.length) {
      segs.push({ type: 'content', blocks: buf });
      buf = [];
    }
  };
  for (const b of blocks) {
    if (b.kind === 'interrupt') {
      flush();
      segs.push({ type: 'interrupt', block: b });
    } else if (b.kind === 'a2ui') {
      flush();
      segs.push({ type: 'a2ui' });
    } else buf.push(b);
  }
  flush();
  return segs;
}

/**
 * Adapt the design-system HilApprovalCard's per-action results into the chat
 * transport's wire decisions ({ type, editedAction?, message? }).
 */
function toWireDecisions(results: HilDecisionResult[]): InterruptDecision[] {
  return results.map((d) => {
    if (d.decision === 'edit') {
      const args = (d.args && typeof d.args === 'object' ? d.args : {}) as Record<string, unknown>;
      return { type: 'edit', editedAction: { name: d.name, args } };
    }
    if (d.decision === 'reject') {
      return { type: 'reject', message: d.reason || undefined };
    }
    return { type: 'approve' };
  });
}

/**
 * The live chat surface, shared by S2 (web app, mode="full") and S3 (widget,
 * mode="panel"). Assembles design-system chat components; owns no atom styling.
 * Kept separate from design-system's static ChatWindow because it needs the
 * follow-scroll MessageList and dynamic composer/abort/error wiring.
 */
export function ChatView({
  mode = 'panel',
  title,
  subtitle,
  messages,
  phase,
  activity,
  interrupt,
  onRespondInterrupt,
  error,
  onSend,
  attach,
  onRetry,
  onAbort,
  onClose,
  quickReplies,
  onQuickReply,
  renderA2ui,
  renderRichContent,
  onRevealFile,
  showHeader = true,
  greeting,
  poweredBy,
  disclaimer,
  className,
}: ChatViewProps) {
  const isPanel = mode === 'panel';
  const running = phase === 'running';
  const interrupted = phase === 'interrupted';
  const busy = running || interrupted;
  const avatar = <Avatar name="L" size={28} />;
  // Invisible spacer that reserves the avatar column, so grouped/resumed bubbles
  // (which don't repeat the avatar) stay aligned with the ones that show it
  // instead of drifting to the left edge.
  const avatarGutter = (
    <span aria-hidden className="invisible">
      {avatar}
    </span>
  );
  const pickReply = (t: string) => (onQuickReply ?? onSend)(t);
  const showPoweredBy = poweredBy ?? isPanel;
  const showFooter = showPoweredBy || Boolean(disclaimer);
  const panelEmpty = isPanel && messages.length === 0;
  // Only the newest un-consumed A2UI surface stays interactive; older/used ones
  // freeze (kept as a record). -1 when there's no live surface.
  const liveSurfaceIndex = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m?.a2ui && m.a2ui.length) return m.a2uiConsumed ? -1 : i;
    }
    return -1;
  })();

  return (
    <div
      className={cn(
        'flex max-w-full flex-col overflow-hidden bg-surface',
        isPanel
          ? 'h-[var(--panel-max-height)] w-[var(--panel-width)] rounded-[var(--panel-radius)] border border-border shadow-panel'
          : 'size-full',
        className,
      )}
    >
      {showHeader ? (
        <ChatHeader title={title} subtitle={subtitle} mode={mode} onClose={onClose} />
      ) : null}

      {!isPanel && messages.length === 0 ? (
        <EmptyHero starters={quickReplies ?? []} onPick={pickReply} />
      ) : (
        <MessageList contentClassName={isPanel ? undefined : cn(COLUMN, 'py-6')}>
          {panelEmpty && (greeting || (quickReplies && quickReplies.length > 0)) ? (
            <div className="flex flex-col gap-[var(--space-2-5)]">
              {greeting ? (
                <MessageBubble role="assistant" showTail avatar={avatar}>
                  {greeting}
                </MessageBubble>
              ) : null}
              {quickReplies && quickReplies.length > 0 ? (
                <QuickReplies items={quickReplies} onSelect={pickReply} className="pl-9" />
              ) : null}
            </div>
          ) : null}
          {messages.map((m, i) => {
            if (m.role === 'user') {
              return (
                <div key={i} className="flex w-full flex-col gap-1">
                  {m.attachments?.length ? <UserAttachments items={m.attachments} /> : null}
                  {m.text ? (
                    <MessageBubble role="user" className="w-full">
                      {m.text}
                    </MessageBubble>
                  ) : null}
                </div>
              );
            }
            // Assistant turn: ordered blocks (deep agents interleave text + activity + HIL).
            const blocks: MessageBlock[] =
              m.blocks && m.blocks.length
                ? m.blocks
                : m.text
                  ? [{ kind: 'text', text: m.text }]
                  : [];
            const hasInterrupt = blocks.some((b) => b.kind === 'interrupt');
            const hasA2ui = !!(m.a2ui && m.a2ui.length && renderA2ui);
            const hasBubbleContent = blocks.some(
              (b) =>
                (b.kind === 'text' && b.text) ||
                // A settled trace OR a still-live block that has started streaming
                // sub-event chunks (reasoning / tool args) — the latter lets the
                // leading block render its live ProcessDisclosure instead of hiding
                // behind the bare standalone spinner until it settles.
                (b.kind === 'activity' &&
                  (b.state === 'done' || !!(b.chunks && b.chunks.length > 0))) ||
                b.kind === 'file',
            );

            // Before any settled content (and no pending card / surface): standalone indicator.
            if (!hasBubbleContent && !hasInterrupt && !hasA2ui) {
              if (!m.streaming) return null;
              const trailing = [...blocks].reverse().find((b) => b.kind === 'activity') as
                ActivityBlock | undefined;
              const act = trailing ?? activity ?? { status: 'thinking' as const };
              return (
                <ActivityIndicator
                  key={i}
                  status={act.status}
                  detail={act.detail}
                  startedAt={trailing?.startedAt}
                  variant={isPanel ? 'bubble' : 'bare'}
                  glyph={act.status === 'writing' ? 'dots' : 'spinner'}
                  avatar={avatar}
                />
              );
            }

            // Split at inline interrupt + A2UI markers so cards/surfaces sit in
            // chronological order: process trace → surface/card → resumed answer.
            const segments = splitSegments(blocks);
            // The turn's A2UI surface, aligned + constrained like the HIL card
            // (avatar gutter, capped at bubble width). Frozen once superseded.
            const a2uiNode = hasA2ui ? (
              <div className="flex w-full items-start gap-2">
                {avatarGutter}
                <div
                  className={cn(
                    'w-[var(--bubble-max-width)] min-w-0 max-w-full',
                    i !== liveSurfaceIndex && 'opacity-70',
                  )}
                  // Frozen surface: block all interaction (pointer/keyboard/focus)
                  // so it stays a read-only record and can't re-fire its action.
                  inert={i !== liveSurfaceIndex ? true : undefined}
                >
                  {renderA2ui!(m.a2ui!)}
                </div>
              </div>
            ) : null;
            let a2uiRendered = false;
            return (
              <div key={i} className="flex flex-col gap-1">
                {segments.map((seg, si) => {
                  if (seg.type === 'a2ui') {
                    if (!a2uiNode || a2uiRendered) return null;
                    a2uiRendered = true;
                    return <React.Fragment key={`a2ui-${si}`}>{a2uiNode}</React.Fragment>;
                  }
                  if (seg.type === 'interrupt') {
                    // After submit, keep the card in its "제출됨 · 처리 중…" spinner
                    // state until the resumed run emits its next event (renderable
                    // content/surface below this marker); then it settles to the ✓
                    // decision trace — so it flips "자연스럽게" instead of instantly.
                    const awaitingResume =
                      busy &&
                      !segments
                        .slice(si + 1)
                        .some(
                          (s) =>
                            s.type === 'a2ui' ||
                            (s.type === 'content' && buildTraceItems(s.blocks).length > 0),
                        );
                    return (
                      <div key={`hil-${seg.block.id}`} className="flex w-full items-start gap-2">
                        {avatarGutter}
                        <div className="w-[var(--bubble-max-width)] min-w-0 max-w-full">
                          <HilApprovalCard
                            actions={seg.block.actions}
                            mode={isPanel ? 'panel' : 'full'}
                            pending={awaitingResume}
                            onDecide={(results) => onRespondInterrupt?.(toWireDecisions(results))}
                          />
                        </div>
                      </div>
                    );
                  }
                  const items = buildTraceItems(seg.blocks, onRevealFile);
                  if (!items.length) return null;
                  const lastIsText = seg.blocks[seg.blocks.length - 1]?.kind === 'text';
                  const isLastSeg = si === segments.length - 1;
                  return (
                    <MessageBubble
                      key={`seg-${si}`}
                      role="assistant"
                      streaming={m.streaming && lastIsText && isLastSeg}
                      showTail
                      avatar={si === 0 && showsAvatar(messages, i) ? avatar : avatarGutter}
                    >
                      <ActivityTrace items={items} />
                    </MessageBubble>
                  );
                })}
                {/* Fallback: surface present but no positional marker (e.g. a
                    restored/hydrated transcript) → render at the turn bottom. */}
                {a2uiNode && !a2uiRendered ? a2uiNode : null}
                {m.cites && m.cites.length ? (
                  <div className="flex flex-wrap gap-1 pl-9">
                    {m.cites.map((c) => (
                      <a
                        key={c.num}
                        href={c.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 rounded-pill bg-accent-soft px-2 py-0.5 text-2xs text-accent"
                      >
                        <span className="font-bold">{c.num}</span>
                        {c.title || c.url}
                      </a>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}

          {renderRichContent ? <div className="pl-9">{renderRichContent()}</div> : null}

          {/* Defensive fallback: interrupt set but not carried inline in a message. */}
          {interrupt &&
          !messages.some((m) =>
            m.blocks?.some((b) => b.kind === 'interrupt' && b.id === interrupt.id),
          ) ? (
            <div className="flex w-full items-start gap-2">
              {avatarGutter}
              <div className="w-[var(--bubble-max-width)] min-w-0 max-w-full">
                <HilApprovalCard
                  key={interrupt.id}
                  actions={interrupt.actions}
                  mode={isPanel ? 'panel' : 'full'}
                  pending={busy}
                  onDecide={(results) => onRespondInterrupt?.(toWireDecisions(results))}
                />
              </div>
            </div>
          ) : null}

          {phase === 'error' ? (
            <Banner
              tone="danger"
              title="오류"
              action={
                onRetry ? (
                  <button
                    type="button"
                    onClick={onRetry}
                    className="text-sm font-semibold text-accent underline"
                  >
                    다시 시도
                  </button>
                ) : undefined
              }
            >
              {error ?? '스트리밍 중 오류가 발생했습니다.'}
            </Banner>
          ) : null}
        </MessageList>
      )}

      {isPanel ? (
        <Composer
          onSend={onSend}
          disabled={busy}
          streaming={running}
          onStop={onAbort}
          canAttach={!!attach?.accept}
          attachMode={attach?.mode ?? 'file'}
          accept={attach?.mimeTypes?.length ? attach.mimeTypes.join(',') : undefined}
        />
      ) : (
        <div className={cn(COLUMN, 'py-3')}>
          <Composer
            onSend={onSend}
            disabled={busy}
            streaming={running}
            onStop={onAbort}
            showVoice
            canAttach={!!attach?.accept}
            attachMode={attach?.mode ?? 'file'}
            accept={attach?.mimeTypes?.length ? attach.mimeTypes.join(',') : undefined}
          />
        </div>
      )}

      {showFooter ? (
        <div className="bg-surface pb-[var(--space-1-5)] text-center text-2xs text-fg-muted">
          {disclaimer ? <>{disclaimer} · </> : null}
          {showPoweredBy ? (
            <>
              ⚡ Powered by <b className="text-fg-secondary">Langbridge</b>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
